import { useState } from 'react'
import { Home, UtensilsCrossed, TreePine, Shield, Sparkles, Star, PhoneOff, Compass, Heart } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { getContactPath, getRegistrationPath } from '../utils/routes'
import { useCamps } from '../hooks/useCamps'
import { useHeroes } from '../hooks/useHeroes'
import { useSiteImages } from '../hooks/useSiteImages'
import type { CampData } from '../types/camp'
import Button from '../components/ui/Button'
import SectionHeading from '../components/ui/SectionHeading'
import AnimatedCounter from '../components/ui/AnimatedCounter'
import PlaceholderImage from '../components/ui/PlaceholderImage'
import CampDetailModal from '../components/CampDetailModal'
import DailySchedule from '../components/DailySchedule'
import PhotoGallery from '../components/PhotoGallery'
import Testimonials from '../components/Testimonials'
import FAQ from '../components/FAQ'
import PeopleCarousel from '../components/PeopleCarousel'
import SEOHead from '../components/SEOHead'

const campImages = ['camp', 'nature', 'resort'] as const

const benefitIcons = [Home, UtensilsCrossed, Star, TreePine, Shield, Sparkles]

// Smooth-scroll to an on-page section by id, honouring reduced-motion.
function scrollToSection(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
}

export default function Homepage() {
  const { locale, t } = useLocale()
  const contactPath = getContactPath(locale)
  const registrationPath = getRegistrationPath(locale)
  const { camps: campData, loading: campsLoading } = useCamps()
  const { heroes: cmsHeroes } = useHeroes()
  const { siteImages } = useSiteImages()
  const [selectedCamp, setSelectedCamp] = useState<CampData | null>(null)

  const cardTexts = [t.services.camp1, t.services.camp2, t.services.camp3]

  // First two active camps, by CMS order — drives the two camp cards.
  // Slug-independent so it works regardless of the CMS-assigned ids.
  const activeCamps = campData.filter((c) => c.active)

  // Group camp label for query param
  const groupCampLabel = locale === 'hu' ? 'Csoportos tábor' : 'Tabără de grup'

  return (
    <>
      <SEOHead title={t.home.metaTitle} description={t.home.metaDescription} />

      {/* 1. Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          {siteImages?.hero ? (
            <img
              src={siteImages.hero}
              alt=""
              width={1920}
              height={1080}
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          ) : (
            <PlaceholderImage
              label="Hero — Children in nature"
              variant="hero"
              className="w-full h-full"
            />
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(31,42,55,0.85)] via-[rgba(31,42,55,0.6)] to-transparent" />
        </div>

        {/* Content */}
        <div className="relative container-max px-4 md:px-8 py-20">
          <div className="max-w-2xl text-center md:text-left">
            <h1 className="text-white mb-6 animate-[fadeSlideUp_0.6s_ease-out_both]">
              {t.hero.headline}
            </h1>
            <p className="text-gray-200 text-lg md:text-xl leading-relaxed mb-8 animate-[fadeSlideUp_0.6s_ease-out_0.2s_both]">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start animate-[fadeSlideUp_0.6s_ease-out_0.4s_both]">
              <Button onClick={() => scrollToSection('taborok')} size="lg">
                {t.hero.cta}
              </Button>
              <Button
                onClick={() => scrollToSection('szamokban')}
                variant="outlineWhite"
                size="lg"
              >
                {t.hero.ctaSecondary}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Track Record (Stats) */}
      <section id="szamokban" className="section-padding bg-white scroll-mt-20">
        <div className="container-max">
          <SectionHeading title={t.socialProof.title} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {t.socialProof.stats.map((stat, i) => (
              <AnimatedCounter
                key={i}
                value={stat.value}
                suffix={stat.suffix}
                label={stat.label}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 3. Digital Detox — Main USP */}
      <section className="section-padding bg-gradient-to-br from-secondary via-secondary-hover to-secondary">
        <div className="container-max">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-white/10 text-white/90 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
              <PhoneOff size={16} />
              {t.digitalDetox.title}
            </span>
            <h2 className="text-white mb-4">{t.digitalDetox.headline}</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
              {t.digitalDetox.text}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.digitalDetox.features.map((feature, i) => {
              const icons = [PhoneOff, Compass, Heart]
              const Icon = icons[i]
              return (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-sm rounded-card p-6 text-center border border-white/10"
                >
                  <div className="w-14 h-14 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={28} className="text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* 4. Heroes — Team Section */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <SectionHeading title={t.heroes.title} subtitle={t.heroes.subtitle} />
          <PeopleCarousel
            people={
              cmsHeroes.length > 0
                ? cmsHeroes.map((h) => ({
                    name: h.name,
                    role: h.role[locale],
                    bio: h.bio[locale],
                    photo: h.photo,
                  }))
                : t.heroes.members.map((m) => ({
                    name: m.name,
                    role: m.role,
                    bio: m.bio,
                    photo: '',
                  }))
            }
          />
        </div>
      </section>

      {/* 5. Camps Section — alternating layout */}
      <section id="taborok" className="section-padding bg-surface scroll-mt-20">
        <div className="container-max">
          <SectionHeading title={t.services.title} subtitle={t.services.subtitle} />

          <div className="space-y-16 md:space-y-24">
            {cardTexts.map((card, i) => {
              const isReversed = i % 2 !== 0
              const matchedCamp = i < 2 ? activeCamps[i] ?? null : null

              // Determine CTA action
              let ctaProps: { onClick?: () => void; to?: string }
              if (i < 2 && matchedCamp) {
                ctaProps = { onClick: () => setSelectedCamp(matchedCamp) }
              } else if (i === 2) {
                ctaProps = { to: `${contactPath}?campType=${encodeURIComponent(groupCampLabel)}` }
              } else {
                ctaProps = { to: contactPath }
              }

              return (
                <div
                  key={i}
                  className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 md:gap-12`}
                >
                  {/* Image */}
                  <div className="w-full md:w-1/2">
                    {!campsLoading && matchedCamp?.image && !matchedCamp.image.includes('placeholder') ? (
                      <img
                        src={matchedCamp.image}
                        alt={card.title}
                        width={800}
                        height={600}
                        className="w-full aspect-[4/3] object-cover rounded-card"
                        loading="lazy"
                      />
                    ) : (
                      <PlaceholderImage
                        label={card.title}
                        variant={campImages[i]}
                        className="w-full aspect-[4/3] rounded-card"
                      />
                    )}
                  </div>

                  {/* Text */}
                  <div className="w-full md:w-1/2">
                    <h3 className="font-heading font-semibold text-xl md:text-2xl text-secondary mb-4">
                      {card.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed mb-6">
                      {card.description}
                    </p>
                    <Button {...ctaProps} size="lg">
                      {card.link}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Camp Detail Modal */}
      <CampDetailModal
        camp={selectedCamp}
        open={!!selectedCamp}
        onClose={() => setSelectedCamp(null)}
      />

      {/* 7. Benefits Section (Why choose us) */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <SectionHeading title={t.benefits.title} subtitle={t.benefits.subtitle} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {t.benefits.items.map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                  {(() => {
                    const Icon = benefitIcons[i]
                    return <Icon size={24} className="text-primary" strokeWidth={1.5} />
                  })()}
                </div>
                <div>
                  <h3 className="font-heading font-medium text-base mb-1 text-secondary">
                    {item.title}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Photo Gallery */}
      <PhotoGallery />

      {/* 9. Parent Testimonials */}
      <Testimonials />

      {/* 10. Location */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <div className="bg-white rounded-card p-8 md:p-12">
            <h3 className="font-heading font-medium text-xl mb-4 text-secondary">
              {t.socialProof.locationTitle}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {(siteImages?.locationPhotos && siteImages.locationPhotos.length > 0
                ? siteImages.locationPhotos
                : [null, null, null, null]
              ).map((src, i) =>
                src ? (
                  <img
                    key={i}
                    src={src}
                    alt={`Resort photo ${i + 1}`}
                    width={400}
                    height={300}
                    className="w-full h-40 object-cover rounded-btn"
                    loading="lazy"
                  />
                ) : (
                  <PlaceholderImage
                    key={i}
                    label={`Resort photo ${i + 1}`}
                    variant={i % 2 === 0 ? 'resort' : 'nature'}
                    className="w-full h-40 rounded-btn"
                  />
                )
              )}
            </div>
            <p className="text-text-secondary mb-4">{t.socialProof.locationText}</p>
            <Button href="https://www.magicharghita.ro" variant="outline">
              {t.socialProof.locationCta}
            </Button>
          </div>
        </div>
      </section>

      {/* 12. Daily Schedule */}
      <DailySchedule />

      {/* 13. FAQ */}
      <FAQ viewAllLink />

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-secondary to-secondary-hover">
        <div className="container-max px-4 md:px-8 text-center">
          <h2 className="text-white mb-4">{t.finalCta.headline}</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">{t.finalCta.subtitle}</p>
          <Button to={registrationPath} size="lg">
            {t.finalCta.cta}
          </Button>
        </div>
      </section>
    </>
  )
}
